import { AutocompleteControl, AutocompleteControlProps, TextFieldControl } from '../form';

import { useMemo } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Countries from '../../ISO3166-1.alpha2.json';

const COUNTRIES = Object.entries(Countries).map(([key, value]) => ({
  value: key,
  ...value,
}));

type CountryAutocompleteControlProps<
  TFieldValues extends FieldValues = FieldValues,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
> = Omit<
  AutocompleteControlProps<TFieldValues, string, Multiple, DisableClearable, false>,
  'autoComplete' | 'autoHighlight' | 'options' | 'getOptionLabel'
> & {
  /**
   * Enables a hidden companion input bound to the same form field as the Autocomplete.
   * This input is used solely to support native browser autofill (e.g. address or country autofill).
   *
   * The input remains mounted in the DOM but is visually hidden, allowing browser autofill engines
   * to detect and populate it while the visible Autocomplete provides the UI.
   *
   * Important:
   * - This improves compatibility with native browser autofill behavior.
   * - Some password managers or autofill tools (e.g. Bitwarden) may ignore hidden or non-visible inputs
   *   due to security heuristics.
   */
  enableBrowserFill?: boolean;
};

export function CountryAutocompleteControl<
  TFieldValues extends FieldValues = FieldValues,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
>(props: CountryAutocompleteControlProps<TFieldValues, Multiple, DisableClearable>) {
  const { control, disabled, name, enableBrowserFill } = props;
  const { i18n } = useTranslation();
  const resolvedLanguage = i18n.resolvedLanguage?.toUpperCase() === 'DE' ? 'DE' : 'EN';

  const options = useMemo(() => {
    return COUNTRIES.map(c => c.value).sort((a, b) => {
      const labelA = COUNTRIES.find(c => c.value === a)?.[resolvedLanguage] ?? a;
      const labelB = COUNTRIES.find(c => c.value === b)?.[resolvedLanguage] ?? b;
      return labelA.localeCompare(labelB);
    });
  }, [resolvedLanguage]);

  return (
    <>
      <AutocompleteControl
        {...props}
        autoComplete
        autoHighlight
        options={options}
        getOptionLabel={(option: string) => COUNTRIES.find(c => c.value === option)?.[resolvedLanguage] ?? option}
      />
      {enableBrowserFill && !disabled && (
        <TextFieldControl
          control={control}
          name={name}
          autoComplete="country"
          /**
           * Hidden input to enable browser autofill.
           * May be ignored by some password managers (e.g. Bitwarden).
           */
          sx={{
            opacity: 0,
            position: 'absolute',
            pointerEvents: 'none',
            width: '1px',
          }}
        />
      )}
    </>
  );
}
